<template>
  <div class="roles-page" :style="{ '--roles-top': viewportTop + 'px' }">
    <div class="page-heading">
      <div>
        <div class="eyebrow">Administration</div>
        <h1>Roles</h1>
        <p>
          Choose a group, choose a role, then click a menu to set its
          permissions.
        </p>
      </div>
      <div class="heading-actions">
        <v-btn
          v-if="canSeePeople"
          outlined
          :disabled="loading || !!loadError || busy"
          @click="showPeople"
          ><v-icon left small>mdi-account-plus-outline</v-icon>Assign
          people</v-btn
        >
        <v-btn
          outlined
          :loading="loading"
          :disabled="busy"
          @click="refreshWorkspace"
          ><v-icon left small>mdi-refresh</v-icon>Refresh</v-btn
        >
      </div>
    </div>
    <v-alert v-if="loadError" type="error" outlined class="mt-5"
      >{{ loadError }}
      <v-btn text small @click="refreshWorkspace">Try again</v-btn></v-alert
    >
    <v-progress-linear
      v-if="loading"
      indeterminate
      class="mt-5"
      aria-label="Loading roles and groups"
    />
    <div class="access-workspace" :aria-busy="loading">
      <section
        class="selection-panel group-panel"
        aria-labelledby="group-heading"
      >
        <div class="panel-heading">
          <span class="step-number">1</span>
          <div>
            <h2 id="group-heading">Role groups</h2>
            <p>Choose your team or group.</p>
          </div>
        </div>
        <div class="panel-tools">
          <v-text-field
            v-model="groupSearch"
            outlined
            dense
            clearable
            hide-details
            label="Find a group"
            prepend-inner-icon="mdi-magnify"
          /><v-btn
            v-if="allowed('User Groups', 'canCreate')"
            text
            small
            color="primary"
            :disabled="loading || !!loadError"
            @click="openGroup()"
            ><v-icon left small>mdi-plus</v-icon>Create group</v-btn
          >
        </div>
        <div class="selection-list" aria-label="Role groups">
          <button
            type="button"
            class="selection-item all-roles"
            :class="{ 'is-selected': selectedGroupId === 'all' }"
            :aria-pressed="selectedGroupId === 'all'"
            :disabled="busy"
            @click="requestSelection('all')"
          >
            <v-icon small>mdi-view-grid-outline</v-icon
            ><span class="item-copy"
              ><strong>All roles</strong
              ><small>Include roles without a group</small></span
            ><span class="count">{{ roles.length }}</span>
          </button>
          <button
            v-for="group in filteredGroups"
            :key="group.id"
            type="button"
            class="selection-item"
            :class="{ 'is-selected': selectedGroupId === group.id }"
            :aria-pressed="selectedGroupId === group.id"
            :disabled="busy"
            @click="requestSelection(group.id)"
          >
            <v-icon small>mdi-account-group-outline</v-icon
            ><span class="item-copy"
              ><strong>{{ group.groupName }}</strong
              ><small
                >{{ (group.roles || []).length }}
                {{ (group.roles || []).length === 1 ? "role" : "roles" }}</small
              ></span
            ><v-icon small>mdi-chevron-right</v-icon>
          </button>
          <p v-if="!filteredGroups.length && !loading" class="empty-copy">
            {{
              groupSearch
                ? "No groups match your search."
                : "No groups yet. Create a group to organize your roles."
            }}
          </p>
        </div>
        <div v-if="selectedGroup" class="panel-footer">
          <p v-if="selectedGroup.description">
            {{ selectedGroup.description }}
          </p>
          <v-btn
            text
            small
            color="primary"
            :disabled="busy"
            @click="openGroup(selectedGroup)"
            >{{
              allowed("User Groups", "canUpdate")
                ? "Edit group & roles"
                : "View group"
            }}</v-btn
          >
        </div>
      </section>

      <section
        class="selection-panel role-panel"
        aria-labelledby="role-heading"
      >
        <div class="panel-heading">
          <span class="step-number">2</span>
          <div>
            <h2 id="role-heading">Roles</h2>
            <p>
              {{
                selectedGroup
                  ? selectedGroup.groupName
                  : "All roles in the system"
              }}
            </p>
          </div>
        </div>
        <div class="panel-tools">
          <v-text-field
            v-model="roleSearch"
            outlined
            dense
            clearable
            hide-details
            label="Find a role"
            prepend-inner-icon="mdi-magnify"
          /><v-btn
            v-if="allowed('Roles', 'canCreate')"
            text
            small
            color="primary"
            :disabled="loading || !!loadError"
            @click="openRole()"
            ><v-icon left small>mdi-plus</v-icon>Create role</v-btn
          >
        </div>
        <div class="selection-list" aria-label="Roles in selected group">
          <button
            v-for="role in filteredRoles"
            :key="role.id"
            type="button"
            class="selection-item"
            :class="{ 'is-selected': selectedRoleId === role.id }"
            :aria-pressed="selectedRoleId === role.id"
            :disabled="busy"
            @click="requestSelection(selectedGroupId, role.id)"
          >
            <v-icon small>mdi-account-key-outline</v-icon
            ><span class="item-copy"
              ><strong>{{ role.roleName }}</strong
              ><small>{{ roleSummary(role) }}</small></span
            ><v-icon small>mdi-chevron-right</v-icon>
          </button>
          <div v-if="!filteredRoles.length && !loading" class="empty-copy">
            <p>
              {{
                roleSearch
                  ? "No roles match your search."
                  : "This group has no roles yet."
              }}
            </p>
            <v-btn
              v-if="selectedGroup && allowed('User Groups', 'canUpdate')"
              small
              text
              color="primary"
              @click="openGroup(selectedGroup)"
              >Choose roles for this group</v-btn
            >
          </div>
        </div>
        <div v-if="selectedRole" class="panel-footer">
          <v-btn
            v-if="allowed('Roles')"
            text
            small
            color="primary"
            :disabled="busy"
            @click="openRole(selectedRole)"
            >{{
              allowed("Roles", "canUpdate") ? "Edit role" : "View role"
            }}</v-btn
          ><v-btn
            v-if="canDeleteRole"
            text
            small
            color="error"
            :disabled="busy"
            @click="
              deleteError = '';
              deleteDialog = true;
            "
            >Delete role</v-btn
          >
        </div>
      </section>

      <section
        class="selection-panel menu-panel"
        aria-labelledby="menu-heading"
      >
        <div class="panel-heading">
          <span class="step-number">3</span>
          <div class="menu-heading-copy">
            <h2 id="menu-heading">Menus &amp; permissions</h2>
            <p>
              {{
                selectedRole
                  ? "Choose pages and tasks for this role."
                  : "Select a role to see its menus."
              }}
            </p>
          </div>
        </div>
        <role-permission-explorer
          v-if="selectedRole && canSeePermissions"
          :key="selectedRoleId + '-' + previewRevision"
          :role="selectedRole"
          :managed="!staticPreview && !!accessPolicyForUser"
          :editable="canEditPermissions && !busy"
          @dirty-change="permissionPreviewDirty = $event"
        />
        <div v-else class="permission-empty">
          <v-icon size="40">mdi-gesture-tap</v-icon>
          <h3>
            {{
              selectedRole ? "Permissions are view-restricted" : "Choose a role"
            }}
          </h3>
          <p>
            {{
              selectedRole
                ? "Your account can manage the groups and roles allowed to you."
                : "Select a role in the middle column, then click a menu to set its permissions."
            }}
          </p>
        </div>
      </section>
    </div>

    <v-dialog v-model="roleDialog" persistent max-width="540"
      ><v-card v-if="roleDraft" class="editor-card"
        ><v-card-title
          ><h2>{{ roleDraft.id ? "Edit role" : "Create role" }}</h2>
          <v-spacer /><v-btn
            icon
            aria-label="Close role"
            :disabled="busy"
            @click="closeRole"
            ><v-icon>mdi-close</v-icon></v-btn
          ></v-card-title
        ><v-divider /><v-card-text class="editor-body"
          ><v-alert v-if="editorError" type="error" text>{{
            editorError
          }}</v-alert
          ><v-text-field
            v-model="roleDraft.roleName"
            outlined
            label="Role name"
            placeholder="For example, Training coordinator"
            :readonly="!canEditRoleDetails"
            maxlength="100"
            :error-messages="nameError"
            @input="nameError = ''"
          />
          <p v-if="!roleDraft.id">
            New roles start with no access. After creating it, choose its menus
            and permissions on this page. Use Edit group &amp; roles to add it
            to a group.
          </p></v-card-text
        ><v-card-actions class="editor-actions"
          ><v-btn text :disabled="busy" @click="closeRole">Cancel</v-btn
          ><v-spacer /><v-btn
            v-if="canEditRoleDetails"
            color="primary"
            depressed
            :loading="busy"
            :disabled="!roleDirty"
            @click="saveRole"
            >{{ roleDraft.id ? "Save role" : "Create role" }}</v-btn
          ></v-card-actions
        ></v-card
      ></v-dialog
    >

    <v-dialog v-model="groupDialog" persistent max-width="640"
      ><v-card v-if="groupDraft" class="editor-card"
        ><v-card-title
          ><h2>{{ groupDraft.id ? "Edit group & roles" : "Create group" }}</h2>
          <v-spacer /><v-btn
            icon
            aria-label="Close group"
            :disabled="busy"
            @click="closeGroup"
            ><v-icon>mdi-close</v-icon></v-btn
          ></v-card-title
        ><v-divider /><v-card-text class="editor-body"
          ><v-alert v-if="editorError" type="error" text>{{
            editorError
          }}</v-alert
          ><v-text-field
            v-model="groupDraft.groupName"
            outlined
            label="Group name"
            placeholder="For example, Regional training team"
            :readonly="!canChangeGroupDetails"
            maxlength="100"
            :error-messages="nameError"
            @input="nameError = ''" /><v-textarea
            v-model="groupDraft.description"
            outlined
            label="Description (optional)"
            rows="2"
            :readonly="!canChangeGroupDetails" /><v-autocomplete
            v-model="groupDraft.groupRoleIds"
            :items="roles"
            item-text="roleName"
            item-value="id"
            label="Roles available in this group"
            outlined
            multiple
            chips
            deletable-chips
            :readonly="!canMapGroupRoles"
            hint="Choose the roles members of this group can be assigned."
            persistent-hint /></v-card-text
        ><v-card-actions class="editor-actions"
          ><v-btn text :disabled="busy" @click="closeGroup">Cancel</v-btn
          ><v-spacer /><v-btn
            v-if="canEditGroup"
            color="primary"
            depressed
            :loading="busy"
            :disabled="!groupDirty"
            @click="saveGroup"
            >{{ groupDraft.id ? "Save group" : "Create group" }}</v-btn
          ></v-card-actions
        ></v-card
      ></v-dialog
    >

    <v-dialog v-model="peopleDialog" persistent max-width="1000" scrollable
      ><v-card class="editor-card"
        ><v-card-title
          ><div>
            <h2>Assign people</h2>
            <p>
              {{
                selectedRole
                  ? "Selected role: " + selectedRole.roleName
                  : "Choose a person to update their group and role."
              }}
            </p>
          </div>
          <v-spacer /><v-btn
            icon
            aria-label="Close people"
            :disabled="busy"
            @click="peopleDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          ></v-card-title
        ><v-divider /><v-card-text class="people-body"
          ><v-alert v-if="peopleError" type="error" text
            >{{ peopleError }}
            <v-btn text small @click="loadPeople">Try again</v-btn></v-alert
          ><v-text-field
            v-model="peopleSearch"
            outlined
            dense
            clearable
            hide-details
            label="Find a person by name or username"
            prepend-inner-icon="mdi-magnify"
            class="ma-5"
          /><v-data-table
            :headers="peopleHeaders"
            :items="people.rows"
            :loading="peopleLoading"
            :options.sync="peopleOptions"
            :server-items-length="people.count"
            :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
            no-data-text="No people found."
            ><template v-slot:[`item.person`]="{ item }"
              ><strong>{{ personName(item) }}</strong>
              <div class="muted">{{ item.username }}</div></template
            ><template v-slot:[`item.userGroups.groupName`]="{ item }">{{
              item.userGroups ? item.userGroups.groupName : "No group"
            }}</template
            ><template v-slot:[`item.userRoles.roleName`]="{ item }">{{
              item.userRoles ? item.userRoles.roleName : "No role"
            }}</template
            ><template v-slot:[`item.actions`]="{ item }"
              ><v-btn
                v-if="allowed('Users', 'canUpdate')"
                text
                small
                color="primary"
                @click="openAssignment(item)"
                >Change access</v-btn
              ></template
            ></v-data-table
          ></v-card-text
        ></v-card
      ></v-dialog
    >
    <v-dialog v-model="assignmentDialog" persistent max-width="600"
      ><v-card v-if="assignment" class="editor-card"
        ><v-card-title
          ><h2>Change access</h2>
          <v-spacer /><v-btn
            icon
            aria-label="Close assignment"
            :disabled="busy"
            @click="assignmentDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          ></v-card-title
        ><v-divider /><v-card-text class="editor-body"
          ><v-alert v-if="editorError" type="error" text>{{
            editorError
          }}</v-alert>
          <h3>{{ personName(assignment.person) }}</h3>
          <p class="mb-6">
            Current role:
            {{
              assignment.person.userRoles
                ? assignment.person.userRoles.roleName
                : "No role"
            }}
          </p>
          <v-autocomplete
            v-model="assignment.userGroupId"
            :items="groups"
            item-text="groupName"
            item-value="id"
            outlined
            label="1. Choose a group"
            @change="assignment.userRoleId = null"
          /><v-autocomplete
            v-model="assignment.userRoleId"
            :items="assignmentRoles"
            item-text="roleName"
            item-value="id"
            outlined
            label="2. Choose a role"
            :disabled="!assignment.userGroupId"
            :hint="
              assignment.userGroupId && !assignmentRoles.length
                ? 'This group has no roles. Use Edit group & roles first.'
                : 'Only roles from the selected group are shown.'
            "
            persistent-hint
          /><v-alert text type="info" class="mt-5"
            >This replaces the person's current group and role. Account and
            organization details stay the same.</v-alert
          ><v-alert v-if="isSelfAssignment" text type="warning"
            >You are changing your own access. Make sure the new role still
            allows you to manage administration.</v-alert
          ></v-card-text
        ><v-card-actions class="editor-actions"
          ><v-btn text :disabled="busy" @click="assignmentDialog = false"
            >Cancel</v-btn
          ><v-spacer /><v-btn
            color="primary"
            depressed
            :loading="busy"
            :disabled="
              !assignment.userGroupId ||
              !assignment.userRoleId ||
              !assignmentDirty
            "
            @click="saveAssignment"
            >Save access</v-btn
          ></v-card-actions
        ></v-card
      ></v-dialog
    >
    <v-dialog v-model="deleteDialog" persistent max-width="480"
      ><v-card class="editor-card"
        ><v-card-title>Delete this role?</v-card-title
        ><v-card-text
          ><strong>{{ selectedRole ? selectedRole.roleName : "" }}</strong> will
          be permanently deleted. Remove it from groups and reassign its people
          first.<v-alert v-if="deleteError" class="mt-4" type="error" text>{{
            deleteError
          }}</v-alert></v-card-text
        ><v-card-actions class="editor-actions"
          ><v-btn text :disabled="busy" @click="deleteDialog = false"
            >Keep role</v-btn
          ><v-spacer /><v-btn
            color="error"
            depressed
            :loading="busy"
            @click="deleteRole"
            >Delete role</v-btn
          ></v-card-actions
        ></v-card
      ></v-dialog
    >
    <v-dialog v-model="discardDialog" persistent max-width="440"
      ><v-card class="editor-card"
        ><v-card-title>Discard unsaved changes?</v-card-title
        ><v-card-text>Your changes have not been saved.</v-card-text
        ><v-card-actions class="editor-actions"
          ><v-btn text @click="discardDialog = false">Keep editing</v-btn
          ><v-spacer /><v-btn text color="primary" @click="discardChanges"
            >Discard changes</v-btn
          ></v-card-actions
        ></v-card
      ></v-dialog
    >
  </div>
</template>

<script>
import { RoleService } from "./role.service";
import { UserGroupService } from "../UserGroup/Usergroup.service";
import RolePermissionExplorer from "./RolePermissionExplorer.vue";
import permissionCatalog from "@/config/menu-permissions.json";
import { MenuItemService } from "../MenuItem/menuItem.service";
import { UserService } from "../User/user.service";
import { accessPolicy, actionAllowed } from "@/utils/managedAccess";
import { getUser } from "@/utils/helper";
const clone = (value) => JSON.parse(JSON.stringify(value));
const flags = [
  "canRead",
  "canCreate",
  "canUpdate",
  "canReadOthers",
  "canAccreditCourse",
];
export default {
  components: { RolePermissionExplorer },
  props: { staticPreview: Boolean },
  data() {
    return {
      viewportTop: 0,
      roles: [],
      groups: [],
      loading: false,
      busy: false,
      loadError: "",
      selectedGroupId: null,
      selectedRoleId: null,
      permissionPreviewDirty: false,
      previewRevision: 0,
      pendingSelection: null,
      groupSearch: "",
      roleSearch: "",
      roleDialog: false,
      groupDialog: false,
      peopleDialog: false,
      assignmentDialog: false,
      deleteDialog: false,
      discardDialog: false,
      discardTarget: "",
      roleDraft: null,
      roleOriginal: "",
      groupDraft: null,
      groupOriginal: "",
      editorError: "",
      nameError: "",
      deleteError: "",
      people: { rows: [], count: 0 },
      peopleLoading: false,
      peopleError: "",
      peopleSearch: "",
      peopleTimer: null,
      peopleRequest: 0,
      peopleOptions: { page: 1, itemsPerPage: 10 },
      assignment: null,
      peopleHeaders: [
        { text: "Person", value: "person", sortable: false },
        { text: "Group", value: "userGroups.groupName", sortable: false },
        { text: "Role", value: "userRoles.roleName", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    accessPolicyForUser() {
      return accessPolicy(getUser());
    },
    selectedGroup() {
      return (
        this.groups.find((group) => group.id === this.selectedGroupId) || null
      );
    },
    selectedRole() {
      return this.roles.find((role) => role.id === this.selectedRoleId) || null;
    },
    groupRoles() {
      return this.selectedGroupId === "all"
        ? this.roles
        : this.roles.filter((role) =>
            (this.selectedGroup?.roles || []).some(
              (mapped) => mapped.id === role.id,
            ),
          );
    },
    filteredGroups() {
      const term = (this.groupSearch || "").trim().toLowerCase();
      return this.groups.filter((group) =>
        group.groupName.toLowerCase().includes(term),
      );
    },
    filteredRoles() {
      const term = (this.roleSearch || "").trim().toLowerCase();
      return this.groupRoles.filter((role) =>
        role.roleName.toLowerCase().includes(term),
      );
    },
    canDeleteRole() {
      return accessPolicy(getUser())
        ? actionAllowed(getUser(), "roles", "delete")
        : this.allowed("Roles", "canUpdate");
    },
    canSeePermissions() {
      return this.allowed("Permissions");
    },
    canEditPermissions() {
      return (
        this.canSeePermissions &&
        (this.allowed("Permissions", "canUpdate") ||
          this.allowed("Permissions", "canCreate"))
      );
    },
    canSeePeople() {
      if (accessPolicy(getUser()))
        return (
          actionAllowed(getUser(), "roles", "assignAccess") ||
          actionAllowed(getUser(), "users")
        );
      return this.allowed("Users");
    },
    canEditRoleDetails() {
      return (
        this.roleDraft &&
        this.allowed("Roles", this.roleDraft.id ? "canUpdate" : "canCreate")
      );
    },
    canMapGroupRoles() {
      return accessPolicy(getUser())
        ? actionAllowed(getUser(), "roles", "mapRoles")
        : this.canEditGroup;
    },
    canChangeGroupDetails() {
      return (
        !!this.groupDraft &&
        this.allowed(
          "User Groups",
          this.groupDraft.id ? "canUpdate" : "canCreate",
        )
      );
    },
    canEditGroup() {
      if (accessPolicy(getUser()))
        return (
          this.canChangeGroupDetails ||
          (!!this.groupDraft?.id && this.canMapGroupRoles)
        );
      return (
        this.groupDraft &&
        this.allowed(
          "User Groups",
          this.groupDraft.id ? "canUpdate" : "canCreate",
        )
      );
    },
    permissionDirty() {
      return this.permissionPreviewDirty;
    },
    roleDirty() {
      return (
        !!this.roleDraft && JSON.stringify(this.roleDraft) !== this.roleOriginal
      );
    },
    groupDirty() {
      return (
        !!this.groupDraft &&
        JSON.stringify(this.groupDraft) !== this.groupOriginal
      );
    },
    assignmentRoles() {
      const group =
        this.assignment &&
        this.groups.find(
          (group) => Number(group.id) === Number(this.assignment.userGroupId),
        );
      return group ? group.roles || [] : [];
    },
    assignmentDirty() {
      return (
        this.assignment &&
        (Number(this.assignment.userGroupId) !==
          Number(this.assignment.person.userGroupId) ||
          Number(this.assignment.userRoleId) !==
            Number(this.assignment.person.userRoleId))
      );
    },
    isSelfAssignment() {
      return (
        this.assignment &&
        Number(this.assignment.person.id) === Number(getUser()?.data?.id)
      );
    },
  },
  watch: {
    peopleOptions: {
      deep: true,
      handler() {
        if (this.peopleDialog) this.loadPeople();
      },
    },
    peopleSearch() {
      clearTimeout(this.peopleTimer);
      this.peopleTimer = setTimeout(() => {
        if (this.peopleOptions.page !== 1) this.peopleOptions.page = 1;
        else this.loadPeople();
      }, 350);
    },
  },
  created() {
    this.loadSetup();
  },
  mounted() {
    this.updateViewportTop();
    window.addEventListener("resize", this.updateViewportTop);
    this.viewportObserver = new ResizeObserver(this.updateViewportTop);
    this.viewportObserver.observe(this.$el);
  },
  beforeDestroy() {
    clearTimeout(this.peopleTimer);
    window.removeEventListener("resize", this.updateViewportTop);
    if (this.viewportObserver) this.viewportObserver.disconnect();
  },
  methods: {
    updateViewportTop() {
      this.viewportTop = Math.max(0, this.$el.getBoundingClientRect().top);
    },
    allowed(name, action = "canRead") {
      if (this.staticPreview)
        return (
          ["Roles", "Permissions", "User Groups"].includes(name) &&
          (action === "canRead" ||
            (name === "Permissions" && action === "canUpdate"))
        );
      if (accessPolicy(getUser())) {
        if (name === "Users" && action === "canUpdate")
          return (
            actionAllowed(getUser(), "roles", "assignAccess") ||
            actionAllowed(getUser(), "users", "assignAccess")
          );
        const map = {
          Roles: [
            "roles",
            { canRead: "view", canCreate: "createRole", canUpdate: "editRole" },
          ],
          "User Groups": [
            "roles",
            {
              canRead: "view",
              canCreate: "createGroup",
              canUpdate: "editGroup",
            },
          ],
          Permissions: [
            "roles",
            {
              canRead: "view",
              canCreate: "changePermissions",
              canUpdate: "changePermissions",
            },
          ],
          Users: [
            "users",
            { canRead: "view", canCreate: "create", canUpdate: "assignAccess" },
          ],
        };
        const entry = map[name];
        return !!entry && actionAllowed(getUser(), entry[0], entry[1][action]);
      }
      return (getUser()?.permissions || []).some(
        (permission) =>
          permission.menuItem?.menuItemName === name &&
          permission.canRead &&
          permission[action],
      );
    },
    ensureSuccess(result, fallback) {
      if (!result || result.status !== 200)
        throw new Error(
          typeof result?.message === "string" ? result.message : fallback,
        );
      return result.data;
    },
    errorText(error, fallback) {
      return typeof error?.response?.data?.message === "string"
        ? error.response.data.message
        : error.message && !error.message.startsWith("[RWV]")
          ? error.message
          : fallback;
    },
    roleSummary(role) {
      if (!this.canSeePermissions) return "Select role";
      const count = (role.role_menu_permissions || []).filter(
        (permission) => permission.canRead,
      ).length;
      return count
        ? "Access to " + count + (count === 1 ? " menu" : " menus")
        : "No menu access yet";
    },
    personName(person) {
      return (
        [person.firstName, person.middleName, person.lastName]
          .filter(Boolean)
          .join(" ") || person.username
      );
    },
    applySelection(groupId, roleId) {
      this.selectedGroupId = groupId;
      const available =
        groupId === "all"
          ? this.roles
          : this.roles.filter((role) =>
              (
                this.groups.find((group) => group.id === groupId)?.roles || []
              ).some((mapped) => mapped.id === role.id),
            );
      const role = available.find((role) => role.id === roleId) || available[0];
      this.selectedRoleId = role?.id || null;
      this.permissionPreviewDirty = false;
      this.previewRevision++;
      this.roleSearch = "";
    },
    requestSelection(groupId, roleId) {
      if (
        groupId === this.selectedGroupId &&
        (roleId === undefined || roleId === this.selectedRoleId)
      )
        return;
      if (this.permissionDirty) {
        this.pendingSelection = { groupId, roleId };
        this.discardTarget = "selection";
        this.discardDialog = true;
        return;
      }
      this.applySelection(groupId, roleId);
    },
    refreshWorkspace() {
      if (this.permissionDirty) {
        this.discardTarget = "refresh";
        this.discardDialog = true;
        return;
      }
      this.loadSetup();
    },
    async loadSetup() {
      if (this.staticPreview) {
        const preview = permissionCatalog.previewData;
        this.roles = preview.groups.flatMap((group) =>
          group.roles.map((name) => ({
            id:
              preview.roleIds[name] ||
              "preview-" + name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
            roleName: name,
            role_menu_permissions: preview.defaultMenus.map((menu, index) => ({
              id: "preview-menu-" + index,
              menuItemId: index,
              menuItem: { menuItemName: menu.menuItemName },
              ...Object.fromEntries(flags.map((flag) => [flag, !!menu[flag]])),
            })),
          })),
        );
        this.groups = preview.groups.map((group) => ({
          ...group,
          roles: this.roles.filter((role) =>
            group.roles.includes(role.roleName),
          ),
        }));
        this.applySelection(
          this.selectedGroupId || preview.defaultGroupId,
          this.selectedRoleId || preview.defaultRoleId,
        );
        return;
      }
      this.loading = true;
      this.loadError = "";
      try {
        const [roles, groups] = await Promise.all([
          RoleService.getRoles(),
          UserGroupService.getUserGroups(),
        ]);
        this.roles =
          this.ensureSuccess(roles, "Could not load roles.").rows || [];
        this.groups =
          this.ensureSuccess(groups, "Could not load groups.").rows || [];
        const groupId =
          this.selectedGroupId === "all" ||
          this.groups.some((group) => group.id === this.selectedGroupId)
            ? this.selectedGroupId
            : this.groups[0]?.id || "all";
        this.applySelection(groupId, this.selectedRoleId);
      } catch (error) {
        this.loadError = this.errorText(
          error,
          "Could not load roles and groups. Please try again.",
        );
      } finally {
        this.loading = false;
      }
    },
    openRole(role) {
      if (this.permissionDirty) {
        this.$toast.info(
          "Save or discard your permission preview before editing roles.",
        );
        return;
      }
      this.roleDraft = role
        ? { id: role.id, roleName: role.roleName }
        : { roleName: "" };
      this.roleOriginal = JSON.stringify(this.roleDraft);
      this.nameError = "";
      this.editorError = "";
      this.roleDialog = true;
    },
    openGroup(group) {
      if (this.permissionDirty) {
        this.$toast.info(
          "Save or discard your permission preview before editing groups.",
        );
        return;
      }
      this.groupDraft = group
        ? {
            id: group.id,
            groupName: group.groupName,
            description: group.description || "",
            groupRoleIds: (group.roles || []).map((role) => role.id),
          }
        : { groupName: "", description: "", groupRoleIds: [] };
      this.groupOriginal = JSON.stringify(this.groupDraft);
      this.nameError = "";
      this.editorError = "";
      this.groupDialog = true;
    },
    closeRole() {
      if (this.roleDirty) {
        this.discardTarget = "role";
        this.discardDialog = true;
      } else this.roleDialog = false;
    },
    closeGroup() {
      if (this.groupDirty) {
        this.discardTarget = "group";
        this.discardDialog = true;
      } else this.groupDialog = false;
    },
    discardChanges() {
      this.discardDialog = false;
      if (this.discardTarget === "selection")
        this.applySelection(
          this.pendingSelection.groupId,
          this.pendingSelection.roleId,
        );
      else if (this.discardTarget === "refresh") this.loadSetup();
      else if (this.discardTarget === "role") this.roleDialog = false;
      else this.groupDialog = false;
    },
    validName(name, rows, id, key) {
      if (!name.trim()) {
        this.nameError = "Please enter a name.";
        return false;
      }
      if (
        rows.some(
          (row) =>
            row.id !== id &&
            row[key].trim().toLowerCase() === name.trim().toLowerCase(),
        )
      ) {
        this.nameError = "This name is already in use. Choose another name.";
        return false;
      }
      return true;
    },
    async saveRole() {
      if (
        !this.canEditRoleDetails ||
        !this.validName(
          this.roleDraft.roleName,
          this.roles,
          this.roleDraft.id,
          "roleName",
        )
      )
        return;
      this.busy = true;
      this.editorError = "";
      try {
        const creating = !this.roleDraft.id;
        const roleName = this.roleDraft.roleName.trim();
        if (creating) {
          const menus = accessPolicy(getUser())
            ? { rows: [] }
            : this.ensureSuccess(
                await MenuItemService.getMenuItems(),
                "Could not load available menus.",
              );
          const permissions = (menus.rows || []).map((menu) => ({
            menuItemId: menu.id,
            ...Object.fromEntries(flags.map((flag) => [flag, false])),
          }));
          this.ensureSuccess(
            (await RoleService.create([{ roleName, permissions }])).data,
            "Could not create role.",
          );
        } else
          this.ensureSuccess(
            (await RoleService.update(this.roleDraft.id, { roleName })).data,
            "Could not save role.",
          );
        this.roleDialog = false;
        await this.loadSetup();
        if (creating)
          this.applySelection(
            "all",
            this.roles.find((role) => role.roleName === roleName)?.id,
          );
        this.$toast.success(
          creating
            ? "Role created. Click a menu to set its permissions."
            : "Role saved.",
        );
      } catch (error) {
        this.editorError = this.errorText(
          error,
          "Could not save role. Please try again.",
        );
      } finally {
        this.busy = false;
      }
    },
    async saveGroup() {
      if (
        !this.canEditGroup ||
        !this.validName(
          this.groupDraft.groupName,
          this.groups,
          this.groupDraft.id,
          "groupName",
        )
      )
        return;
      if (!accessPolicy(getUser()) && !this.groupDraft.groupRoleIds.length) {
        this.editorError = "Choose at least one role for this group.";
        return;
      }
      this.busy = true;
      this.editorError = "";
      try {
        const payload = {
          groupName: this.groupDraft.groupName.trim(),
          description: this.groupDraft.description,
          groupRoleIds: this.groupDraft.groupRoleIds,
        };
        const response = this.groupDraft.id
          ? await UserGroupService.update(this.groupDraft.id, payload)
          : await UserGroupService.create(payload);
        this.ensureSuccess(response.data, "Could not save group.");
        this.groupDialog = false;
        await this.loadSetup();
        const group = this.groups.find(
          (group) => group.groupName === payload.groupName,
        );
        if (group) this.applySelection(group.id, this.selectedRoleId);
        this.$toast.success("Group saved.");
      } catch (error) {
        this.editorError = this.errorText(
          error,
          "Could not save group. Please try again.",
        );
      } finally {
        this.busy = false;
      }
    },
    showPeople() {
      this.peopleDialog = true;
      this.loadPeople();
    },
    async loadPeople() {
      if (!this.canSeePeople) return;
      const request = ++this.peopleRequest;
      this.peopleLoading = true;
      this.peopleError = "";
      try {
        const response = await UserService.getUsersWP(
          this.peopleOptions.page,
          this.peopleOptions.itemsPerPage,
          encodeURIComponent((this.peopleSearch || "").trim()),
        );
        const data = this.ensureSuccess(response, "Could not load people.");
        if (request === this.peopleRequest) this.people = data;
      } catch (error) {
        if (request === this.peopleRequest)
          this.peopleError = this.errorText(
            error,
            "Could not load people. Please try again.",
          );
      } finally {
        if (request === this.peopleRequest) this.peopleLoading = false;
      }
    },
    openAssignment(person) {
      const group = this.selectedGroup;
      const role = this.selectedRole;
      this.assignment = {
        person: clone(person),
        userGroupId: group?.id || person.userGroupId,
        userRoleId: group ? role?.id || null : person.userRoleId,
      };
      this.editorError = "";
      this.assignmentDialog = true;
    },
    async saveAssignment() {
      if (
        !this.allowed("Users", "canUpdate") ||
        !this.assignmentRoles.some(
          (role) => Number(role.id) === Number(this.assignment.userRoleId),
        )
      )
        return;
      this.busy = true;
      this.editorError = "";
      try {
        const response = await UserService.update(this.assignment.person.id, {
          userGroupId: this.assignment.userGroupId,
          userRoleId: this.assignment.userRoleId,
        });
        this.ensureSuccess(response.data, "Could not change access.");
        this.assignmentDialog = false;
        this.$toast.success(
          "Access updated. New permissions apply on the next request.",
        );
        await this.loadPeople();
      } catch (error) {
        this.editorError = this.errorText(
          error,
          "Could not change access. Please try again.",
        );
      } finally {
        this.busy = false;
      }
    },
    async deleteRole() {
      if (!this.selectedRole || !this.canDeleteRole) return;
      this.busy = true;
      this.deleteError = "";
      try {
        const response = await RoleService.delete(this.selectedRole.id);
        this.ensureSuccess(response.data, "Could not delete role.");
        this.deleteDialog = false;
        await this.loadSetup();
        this.$toast.success("Role deleted.");
      } catch (error) {
        this.deleteError = this.errorText(
          error,
          "Could not delete role. Please try again.",
        );
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.roles-page {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: calc(100vh - var(--roles-top, 0px));
  height: calc(100dvh - var(--roles-top, 0px));
  overflow: hidden;
  padding: 28px;
  background: #f5f7fb;
  min-height: 0;
  color: #20344a;
  min-width: 0;
}
h1 {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin: 4px 0 8px;
}
h2 {
  font-size: 17px;
  font-weight: 700;
  line-height: 1.4;
}
h3 {
  font-size: 16px;
  font-weight: 600;
}
p {
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
  margin: 3px 0 0;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #60748a;
  font-weight: 700;
}
.page-heading,
.heading-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.page-heading {
  flex-shrink: 0;
  gap: 24px;
}
.roles-page .v-btn,
.editor-card .v-btn {
  text-transform: none;
  letter-spacing: 0;
}
.access-workspace {
  display: grid;
  flex: 1;
  min-height: 0;
  grid-template-columns: 230px 240px minmax(0, 1fr);
  margin-top: 26px;
  border: 1px solid #dde5ef;
  border-radius: 12px;
  background: white;
  overflow: hidden;
}
.selection-panel {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.group-panel,
.role-panel {
  border-right: 1px solid #e1e7ef;
}
.panel-heading {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  min-height: 68px;
  background: #f8fafc;
  border-bottom: 1px solid #e5eaf1;
}
.panel-heading p {
  font-size: 12px;
  overflow-wrap: anywhere;
}
.group-panel .panel-heading,
.role-panel .panel-heading {
  padding: 10px 14px;
  gap: 8px;
}
.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  color: #276cae;
  background: #e9f2fc;
  border: 1px solid #d3e4f7;
  font-size: 12px;
  font-weight: 700;
}
.panel-tools {
  flex-shrink: 0;
  padding: 16px 12px 8px;
}
.panel-tools .v-btn {
  margin-top: 9px;
}
.selection-list {
  flex: 1;
  min-height: 0;
  padding: 0 8px 12px;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.selection-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid transparent;
  padding: 11px 8px;
  text-align: left;
  border-radius: 8px;
  margin: 3px 0;
  color: #34485f;
}
.selection-item:hover {
  background: #f4f8fc;
}
.selection-item.is-selected {
  background: #eaf3ff;
  border-color: #d5e6fb;
  color: #1766b3;
}
.selection-item:focus-visible,
.menu-toggle:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: -2px;
}
.selection-item .v-icon {
  color: #71849a;
}
.selection-item.is-selected .v-icon {
  color: #1976d2;
}
.item-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.item-copy strong {
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}
.item-copy small {
  font-size: 11px;
  color: #738397;
  line-height: 1.5;
}
.all-roles {
  border-bottom-color: #edf0f5;
  margin-bottom: 9px;
}
.count {
  background: #edf1f5;
  padding: 2px 7px;
  border-radius: 6px;
  color: #66788b;
  font-size: 11px;
}
.panel-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  flex-shrink: 0;
  padding: 12px;
  margin-top: auto;
  border-top: 1px solid #e5eaf1;
  background: white;
}
.panel-footer p {
  padding: 0 8px 9px;
  font-size: 12px;
}
.panel-footer .v-btn {
  max-width: 100%;
}
.menu-toolbar {
  padding: 16px 18px;
}
.save-state {
  display: flex;
  gap: 10px;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
}
.save-state span {
  color: #78889a;
  font-size: 11px;
}
.save-state .unsaved {
  color: #a36813;
}
.menu-list {
  padding: 0 16px 16px;
  max-height: 610px;
  overflow-y: auto;
}
.menu-item {
  border: 1px solid #e3e9f0;
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
}
.menu-item--open {
  border-color: #bad4f1;
}
.menu-toggle {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 15px;
  text-align: left;
}
.menu-toggle:hover {
  background: #f7faff;
}
.menu-toggle > .v-icon {
  color: #70859c;
}
.menu-item--open .menu-toggle {
  background: #f0f6ff;
}
.item-copy .access-enabled {
  color: #2f7c63;
}
.change-dot {
  width: 7px;
  height: 7px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #dc971e;
}
.permission-controls {
  padding: 0 16px;
  border-top: 1px solid #dfebf8;
}
.permission-control {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid #edf1f6;
}
.permission-control:last-child {
  border-bottom: 0;
}
.permission-control strong {
  color: #34485f;
  font-size: 13px;
}
.permission-control p {
  font-size: 11px;
}
.permission-switch {
  flex: 0 0 50px;
  min-width: 50px;
  margin: 0;
  padding: 0;
}
.permission-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 14px 18px;
  border-top: 1px solid #e5eaf1;
  margin-top: auto;
  background: #fbfcfe;
}
.permission-note p {
  font-size: 11px;
  margin: 0;
}
.permission-note .v-icon {
  margin-top: 2px;
  color: #7b90a6;
}
.empty-copy {
  padding: 20px 12px;
  color: #76889c;
  font-size: 12px;
}
.permission-empty {
  padding: 90px 28px;
  text-align: center;
}
.permission-empty .v-icon {
  color: #9cafc2;
  margin-bottom: 16px;
}
.permission-empty p {
  margin-top: 10px;
}
.editor-card {
  border-radius: 10px;
  min-width: 0;
}
.editor-card .v-card__title {
  padding: 22px 24px;
}
.editor-body {
  padding: 24px !important;
}
.editor-actions {
  padding: 16px 24px;
}
.people-body {
  padding: 0 !important;
}
.muted {
  font-size: 12px;
  color: #748598;
}
@media (max-width: 1150px) {
  .roles-page {
    padding: 20px;
  }
  .access-workspace {
    grid-template-columns: 200px 210px minmax(0, 1fr);
  }
  .heading-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .save-state {
    align-items: flex-start;
    flex-direction: column;
  }
}
@media (max-width: 900px) {
  .access-workspace {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: minmax(0, 0.45fr) minmax(0, 0.55fr);
  }
  .menu-panel {
    grid-column: 1 / -1;
    border-top: 1px solid #e1e7ef;
  }
  .role-panel {
    border-right: 0;
  }
  .save-state {
    flex-direction: row;
    align-items: center;
  }
  .page-heading {
    flex-wrap: wrap;
  }
}
@media (max-width: 550px) {
  .roles-page {
    padding: 14px;
  }
  .access-workspace {
    margin-top: 12px;
  }
  .group-panel,
  .role-panel {
    border-right: 0;
    border-bottom: 1px solid #e1e7ef;
  }
  .menu-panel {
    border-top: 0;
  }
  .panel-heading {
    padding: 6px 10px;
    min-height: 44px;
  }
  .group-panel .panel-heading,
  .role-panel .panel-heading {
    padding: 6px 10px;
  }
  .panel-heading p,
  .panel-footer p {
    display: none;
  }
  .panel-heading h2 {
    font-size: 14px;
  }
  .panel-tools {
    padding: 8px;
  }
  .panel-footer {
    padding: 6px;
  }
  .heading-actions {
    justify-content: flex-start;
  }
  .save-state {
    flex-wrap: wrap;
  }
  .menu-toolbar {
    padding: 14px;
  }
  .menu-list {
    padding: 0 12px 12px;
    max-height: none;
  }
  .menu-toggle {
    padding: 12px;
  }
  .permission-control {
    gap: 8px;
  }
  .permission-note {
    padding: 12px;
  }
}
</style>
